import React, { forwardRef, useId } from 'react';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { Input } from '@/modules/Input';
import { InputRef } from '@/shared/types/input';
import { InputSize } from '@/shared/types/types';
import { cn } from '@/shared/utils/cn';
import { formatDisplayDateTime } from '../utils/datetimeFormat';

export interface DateTimeReadOnlyInputProps {
  /** Fecha y hora confirmadas mostradas en el campo (solo lectura). */
  value: Date | null;
  /** Texto mostrado en lugar de formatear `value` (p. ej. rango datetime). */
  displayValue?: string;
  placeholder?: string;
  isOpen: boolean;
  onInputClick: () => void;
  className?: string;
  disabled?: boolean;
  label?: string;
  size?: InputSize;
  showSeconds?: boolean;
}

/**
 * Campo de fecha y hora de solo lectura; abre el popup al hacer clic en el input o en el icono.
 *
 * La edición ocurre únicamente en el popup (calendario + {@link TimePickerPanel}).
 *
 * @example
 * ```tsx
 * <DateTimeReadOnlyInput
 *   value={selectedDate}
 *   isOpen={isOpen}
 *   onInputClick={openPicker}
 *   label="Fecha y hora"
 * />
 * ```
 */
export const DateTimeReadOnlyInput = forwardRef<InputRef, DateTimeReadOnlyInputProps>(
  (
    {
      value,
      displayValue,
      placeholder = 'Selecciona fecha y hora',
      isOpen,
      onInputClick,
      className = '',
      disabled = false,
      label = '',
      size = 'sm',
      showSeconds = true,
    },
    ref
  ) => {
    const uniqueId = useId();
    const displayText =
      displayValue !== undefined
        ? displayValue
        : formatDisplayDateTime(value, { showSeconds });

    const handleOpen = () => {
      if (!disabled) {
        onInputClick();
      }
    };

    return (
      <Input
        id={`datetime-readonly-input-${uniqueId}`}
        label={label}
        ref={ref}
        value={displayText}
        size={size}
        placeholder={placeholder}
        aria-expanded={isOpen}
        onClick={handleOpen}
        onClickExtraSlot={handleOpen}
        extraSlot={faCalendar}
        readOnly
        caret={false}
        fullWidth
        disabled={disabled}
        className={cn(!disabled && 'ui-cursor-pointer', className)}
      />
    );
  }
);

DateTimeReadOnlyInput.displayName = 'DateTimeReadOnlyInput';
